<!-- 核销记录的统计、筛选、列表与分页展示。查询和分页由父页面处理。 -->
<template>
  <section class="verification-panel">
    <div class="summary-grid">
      <article v-for="stat in stats" :key="stat.label" class="summary-card">
        <div>
          <span>{{ stat.label }}</span>
          <strong>{{ stat.value }}</strong>
        </div>
        <small :class="`is-${stat.tone ?? 'brand'}`">{{ stat.sub }}</small>
      </article>
    </div>

    <article class="content-card">
      <div class="filter-bar">
        <el-input v-model.trim="filters.code" clearable placeholder="核销码" class="filter-control" @keyup.enter="emit('search')" />
        <el-input v-model.trim="filters.orderNo" clearable placeholder="订单号" class="filter-control" @keyup.enter="emit('search')" />
        <div class="filter-row">
          <el-select v-model="filters.result" clearable placeholder="核销结果" class="filter-control">
            <el-option label="成功" value="SUCCESS" />
            <el-option label="失败" value="FAIL" />
            <el-option label="重复核销" value="DUPLICATE" />
          </el-select>
        </div>
        <div class="filter-bar__actions">
          <button class="search-button" @click="emit('search')">搜索</button>
          <button class="reset-button" @click="emit('reset')">重置</button>
        </div>
      </div>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>核销码</th>
              <th>订单号</th>
              <th>门店</th>
              <th>核销用户</th>
              <th>结果</th>
              <th>失败原因</th>
              <th>核销时间</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in page?.list ?? []" :key="item.id">
              <td><span class="cell-text">{{ item.code }}</span></td>
              <td><span class="cell-text">{{ item.orderNo || item.orderId || '--' }}</span></td>
              <td><span class="cell-text">{{ item.storeName || item.storeId || '--' }}</span></td>
              <td><span class="cell-text">{{ item.userNickname || item.userId || '--' }}</span></td>
              <td><span :class="['status-tag', toneClass(verificationResultTone(item.result))]">{{ getVerificationResultText(item.result) }}</span></td>
              <td><span class="cell-text cell-text--wrap">{{ item.failReason || '--' }}</span></td>
              <td>{{ item.createdAt || '--' }}</td>
            </tr>
            <tr v-if="!(page?.list.length) && !loading"><td colspan="7"><el-empty description="暂无核销记录" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-if="page" class="table-footer">
        <span>共 {{ page.total }} 条记录，第 {{ page.page }}/{{ totalPages }} 页</span>
        <div>
          <button :disabled="page.page <= 1" @click="emit('changePage', page.page - 1)">上一页</button>
          <button class="is-active">{{ page.page }}</button>
          <button :disabled="!page.hasMore" @click="emit('changePage', page.page + 1)">下一页</button>
        </div>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { toRefs } from 'vue'
import { Search } from '@element-plus/icons-vue'
import type { VerificationLogsParams } from '@/api/verification'
import type { OperationStat } from '@/data/operationDesign'

type VerificationPage = Awaited<ReturnType<typeof import('@/api/verification').fetchVerificationLogs>>
type StatusTone = 'brand' | 'success' | 'warning' | 'error' | 'muted'

const props = defineProps<{
  stats: OperationStat[]
  page?: VerificationPage
  filters: VerificationLogsParams
  loading: boolean
  totalPages: number
  getVerificationResultText: (result?: string) => string
  verificationResultTone: (result?: string) => StatusTone
  toneClass: (tone: StatusTone) => string
}>()

const emit = defineEmits<{
  search: []
  reset: []
  changePage: [page: number]
}>()

const {
  stats,
  page,
  filters,
  loading,
  totalPages,
  getVerificationResultText,
  verificationResultTone,
  toneClass,
} = toRefs(props)
</script>

<style scoped lang="scss">
.verification-panel { display: flex; flex-direction: column; gap: 16px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.summary-card { min-height: 102px; padding: 20px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.summary-card div { min-width: 0; }
.summary-card span { display: block; margin-bottom: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.summary-card strong { color: var(--jf-color-text-primary); font-size: 26px; line-height: 1.1; word-break: break-word; }
.summary-card small { padding: 4px 8px; border-radius: 6px; background: var(--jf-color-background); font-size: 12px; white-space: nowrap; }
.content-card { border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); overflow: hidden; }
.filter-bar { min-height: 66px; padding: 14px 16px; border-bottom: 1px solid var(--jf-color-border); display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.filter-control { width: 220px; }
.filter-row { display: flex; gap: 12px; }
.filter-row .filter-control { min-width: 124px; }
.filter-bar__actions { display: flex; gap: 8px; margin-left: auto; }
button { border: 0; cursor: pointer; font: inherit; }
.search-button, .reset-button { height: 36px; padding: 0 16px; border-radius: 8px; font-size: 14px; font-weight: 700; }
.search-button { background: var(--jf-color-primary); color: #fff; }
.reset-button { border: 1px solid var(--jf-color-border); background: #fff; color: var(--jf-color-text-primary); }
.table-wrap { width: 100%; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 14px; }
thead { background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); font-size: 12px; }
th, td { padding: 14px 16px; text-align: left; border-bottom: 1px solid var(--jf-color-border); }
th { font-weight: 700; }
tbody tr:hover { background: rgba(252, 250, 246, .75); }
.cell-text { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
.cell-text--wrap { max-width: 320px; white-space: normal; word-break: break-all; }
.status-tag { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.is-brand { background: rgba(118, 183, 232, .14); color: var(--jf-color-primary-dark); }
.is-success { background: rgba(69, 185, 124, .12); color: var(--jf-color-success); }
.is-warning { background: rgba(242, 169, 80, .14); color: var(--jf-color-warning); }
.is-error { background: rgba(231, 101, 101, .12); color: var(--jf-color-error); }
.is-muted { background: #f1f3f5; color: var(--jf-color-text-secondary); }
.table-footer { padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 16px; color: var(--jf-color-text-secondary); font-size: 14px; }
.table-footer div { display: flex; gap: 6px; }
.table-footer button { min-width: 32px; height: 32px; padding: 0 10px; border: 1px solid var(--jf-color-border); border-radius: 8px; background: #fff; color: var(--jf-color-text-secondary); }
.table-footer .is-active { border-color: var(--jf-color-primary); background: var(--jf-color-primary); color: #fff; }
.table-footer button:disabled { cursor: not-allowed; opacity: .5; }
@media (max-width: 1180px) {
  .summary-grid { grid-template-columns: 1fr; }
}
</style>
